import { ArrowRight, Info } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../intelligence-briefing-library/components/ui/tooltip";
import type { LevelMixRow } from "../../Store/Reducers/MyPortfolioOverviewSlice";

/** RL Levels Mix colors (unchanged). AL Levels Mix reuses these by band. */
const MIX_BAND_COLOR = {
  unassessed: "text-gray-400",
  weak: "text-red-600",
  moderate: "text-amber-600",
  strong: "text-green-600",
} as const;

const AL_COLS = [
  {
    key: "unk",
    label: "Unassessed (AL ?)",
    color: MIX_BAND_COLOR.unassessed,
  },
  { key: "al0", label: "AL 0", color: MIX_BAND_COLOR.weak },
  { key: "al1", label: "AL 1", color: MIX_BAND_COLOR.weak },
  { key: "al2", label: "AL 2", color: MIX_BAND_COLOR.moderate },
  { key: "al3", label: "AL 3", color: MIX_BAND_COLOR.moderate },
  { key: "al4", label: "AL 4", color: MIX_BAND_COLOR.strong },
  { key: "al5", label: "AL 5", color: MIX_BAND_COLOR.strong },
];

const RL_COLS = [
  {
    key: "unassessed",
    label: "Unassessed (RL ?)",
    color: MIX_BAND_COLOR.unassessed,
  },
  { key: "weak", label: "Weak (RL 0–1)", color: MIX_BAND_COLOR.weak },
  {
    key: "moderate",
    label: "Moderate (RL 2–3)",
    color: MIX_BAND_COLOR.moderate,
  },
  { key: "strong", label: "Strong (RL 4–5)", color: MIX_BAND_COLOR.strong },
];

const mixCellBase = "border border-gray-200 px-1.5 py-1";
const mixRowLabelClass = `${mixCellBase} whitespace-nowrap text-left font-semibold text-gray-900`;
const mixCornerCellClass = `${mixCellBase} text-left align-top`;
const mixDataCellClass = `${mixCellBase} min-w-[48px] text-center tabular-nums`;
const mixHeaderCellClass = `${mixCellBase} min-w-[48px] whitespace-nowrap text-center`;

const linkBtnClass =
  "inline-flex cursor-pointer items-center border-none bg-transparent p-0 text-[10.5px] font-semibold tracking-wide text-purple-600 hover:underline";

const mixTooltipClass =
  "z-[9999] max-w-[280px] border border-gray-200 bg-white px-3 py-2.5 text-[12px] font-normal normal-case tracking-normal leading-relaxed text-gray-700 shadow-[0_4px_16px_rgba(0,0,0,0.12)]";

const MIX_INFO = {
  al: "Shows how your accounts are spread across Account Levels: Unassessed (AL ?), AL 0–AL 5.",
  rl: "Shows account breakdown by Relationship Level grouped in 4 bands: Unassessed (RL ?), Weak, Moderate, Strong.",
} as const;

const hlCell = (active: boolean) =>
  active ? "!bg-purple-100 !border-purple-200 font-semibold" : "";

const fmtCell = (
  row: LevelMixRow,
  key: string,
  showPercentage: boolean
): string => {
  if (showPercentage) {
    const pct = row.values?.[key];
    if (pct === undefined || pct === 0) return "–";
    return `${pct}%`;
  }
  const count = row.counts?.[key];
  if (count == null) {
    // Fallback if older API payloads lack counts
    const pct = row.values?.[key];
    if (pct === undefined || pct === 0 || !row.total) return "–";
    return String(Math.round((pct / 100) * row.total));
  }
  if (count === 0) return "–";
  return String(count);
};

type Col = { key: string; label: string; color: string };

const MixTitleWithInfo = ({
  title,
  tooltip,
}: {
  title: string;
  tooltip: string;
}) => (
  <Tooltip>
    <span className="inline-flex items-center gap-1 text-[10.5px] font-bold leading-none tracking-wide text-gray-900">
      {title}
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex shrink-0 cursor-default items-center justify-center border-0 bg-transparent p-0 leading-none text-gray-400 transition-colors hover:text-purple-600 focus:outline-none focus-visible:text-purple-600 data-[state=open]:text-purple-600"
          style={{ width: "1em", height: "1em" }}
          aria-label={`About ${title}`}
        >
          <Info
            aria-hidden
            strokeWidth={2.25}
            className="block"
            style={{ width: "1em", height: "1em" }}
          />
        </button>
      </TooltipTrigger>
    </span>
    <TooltipContent
      side="bottom"
      align="start"
      sideOffset={6}
      className={mixTooltipClass}
    >
      {tooltip}
    </TooltipContent>
  </Tooltip>
);

const MixTable = ({
  id,
  cols,
  rows,
  highlightKey,
  showPercentage,
  cornerTitle,
  cornerTooltip,
}: {
  id: string;
  cols: Col[];
  rows: LevelMixRow[];
  highlightKey?: string | null;
  showPercentage: boolean;
  cornerTitle: string;
  cornerTooltip: string;
}) => (
  <table className="w-full border-collapse text-[10.5px]" id={id}>
    <thead>
      <tr>
        <th className={`${mixCornerCellClass} w-[80px]`} scope="col">
          <MixTitleWithInfo title={cornerTitle} tooltip={cornerTooltip} />
        </th>
        {cols.map(c => (
          <th
            key={c.key}
            className={`${mixHeaderCellClass} ${c.color} ${hlCell(highlightKey === c.key)}`}
            title={
              c.key === "unk" || c.key === "unassessed" ? c.label : undefined
            }
          >
            {c.label}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map(row => (
        <tr key={row.label}>
          <td className={mixRowLabelClass}>{row.label}</td>
          {cols.map(c => (
            <td
              key={c.key}
              className={`${mixDataCellClass} ${c.color} ${hlCell(highlightKey === c.key)}`}
            >
              {fmtCell(row, c.key, showPercentage)}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

interface Props {
  alRows: LevelMixRow[];
  rlRows: LevelMixRow[];
  highlightAlKey?: string | null;
  highlightRlKey?: string | null;
  showRlNudge?: boolean;
  showPercentage: boolean;
  onGoToAccounts?: () => void;
  onGoToRelationships?: () => void;
}

const LevelMixStrip = ({
  alRows,
  rlRows,
  highlightAlKey,
  highlightRlKey,
  showRlNudge,
  showPercentage,
  onGoToAccounts,
  onGoToRelationships,
}: Props) => {
  return (
    <TooltipProvider delayDuration={150}>
      <div className="mb-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="grid gap-0 border-b border-gray-100 lg:grid-cols-2">
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 lg:border-b-0">
            <h3 className="text-[12px] font-bold tracking-wide text-gray-900">
              Levels Mix
            </h3>
            {onGoToAccounts ? (
              <button
                type="button"
                onClick={onGoToAccounts}
                className={linkBtnClass}
              >
                Account Priorities
                <ArrowRight className="ml-0.5 inline h-3 w-3" aria-hidden />
              </button>
            ) : null}
          </div>
          <div className="flex items-center justify-end px-3 py-2">
            {onGoToRelationships ? (
              <button
                type="button"
                onClick={onGoToRelationships}
                className={linkBtnClass}
              >
                Relationship Priorities
                <ArrowRight className="ml-0.5 inline h-3 w-3" aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
        <div className="grid gap-0 lg:grid-cols-2">
          <div
            className="min-w-0 overflow-x-auto border-b border-gray-100 px-3 py-2.5 lg:border-b-0"
            id="al-mix-strip"
          >
            <MixTable
              id="al-mix-table"
              cols={AL_COLS}
              rows={alRows}
              highlightKey={highlightAlKey}
              showPercentage={showPercentage}
              cornerTitle="AL"
              cornerTooltip={MIX_INFO.al}
            />
          </div>

          <div
            className="min-w-0 overflow-x-auto px-3 py-2.5"
            id="rl-mix-strip"
          >
            <MixTable
              id="rl-mix-table"
              cols={RL_COLS}
              rows={rlRows}
              highlightKey={highlightRlKey}
              showPercentage={showPercentage}
              cornerTitle="RL"
              cornerTooltip={MIX_INFO.rl}
            />
            {showRlNudge && onGoToRelationships ? (
              <button
                type="button"
                className="mt-1.5 inline-flex cursor-pointer items-center gap-0.5 border-none bg-transparent p-0 text-left text-[10px] font-semibold text-purple-600 hover:underline"
                onClick={onGoToRelationships}
              >
                See Relationship Priorities
                <ArrowRight className="h-3 w-3 shrink-0" aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default LevelMixStrip;
